/**
 * ShiftReportPage — V2 orchestrator for the Shift Event Breakdown Report.
 *
 * Per-shift breakdown of the same event population Alert Details reads, bucketed
 * by the hour the event started in campus-local time. See
 * docs/SHIFT_REPORT_PLAN.md.
 *
 * Modelled on CaregiverReportPage and reuses rather than rebuilds:
 *   - `ar-*` page chrome from alert-details-page.css (header, time badge and
 *     popover, campus selector). Every report page shares these.
 *   - `ReportFilterDrawer` and `ScheduleReportDrawer` from alert-details. All
 *     three reports read the same events through the same filters, so a fork of
 *     either drawer would be a fork of what the reports mean.
 *
 * `sr-*` classes exist only for what is genuinely new: the transposed metrics
 * grid, the hour chart, the handover panel and the reason grid.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    getCampusConfig,
    updateSessionCampus,
    getUsername,
    getCampusTzOffset,
} from '../../../services/session.js';
import {
    iAmHigherAuthority,
    formatDateTimeIso,
    formatDateTime12Hour,
    getToday,
} from '../../../libs.jsx';
import { fetchShiftReport } from './api/shiftReportApi.js';
import { exportShiftCsv, exportShiftPdf } from '../../../services/csvExport.js';
import { fetchFilterOptions } from '../alert-details/api/alertDetailsApi.js';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import useReportRestore from '../../../hooks/useReportRestore.js';
import { ReportRestoreOffer, ReportRestoreNote }
    from '../../../components/v2/ReportRestoreBanner.jsx';
import ShiftMetricsTable from './components/ShiftMetricsTable.jsx';
import ShiftHourChart from './components/ShiftHourChart.jsx';
import ShiftChangePanel from './components/ShiftChangePanel.jsx';
import ShiftReasonTable from './components/ShiftReasonTable.jsx';
import { formatCount, formatMinutes } from '../../../utils/shiftFormat.js';
import {
    DEFAULT_TIME_WINDOW, timeWindowToValues, describeTimeWindow,
} from '../../../utils/reportTimeWindow.js';

/* The `ar-*` page chrome this page reuses. Imported explicitly even though the
   app already bundles it via AlertDetailsPage — this page's layout depends on it
   directly, so the dependency is stated rather than inherited. Vite dedupes. */
import '../alert-details/alert-details-page.css';
import './shift-report-page.css';
import { expandReasonIds } from '../../../utils/reasonFilterIds.js';
import { REPORT_TYPE_ID as REPORT_TYPE_IDS } from '../../../utils/reportTypes.js';
import { countActiveFilters } from '../../../utils/reportFilterChips.js';
import { dropHiddenEventTypes } from '../../../utils/reportFilterOptions.js';
import { TIME_RANGES, initialRangeState, rangeStateFor, reportErrorMessage, clearCampusFilters } from '../../../utils/reportPageState.js';
import HeaderInfoTip from '../../../components/v2/HeaderInfoTip.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------


/* Cache scope. A literal, not the route param: rename a route and a
   param-derived key silently misses every cached report. */
const REPORT_TYPE = 'shifts';


/* null => the drawer's full default set. Areas, residents, devices, events and
   reasons all narrow the event population, which is what this report
   aggregates, so all of them are meaningful. */
const FILTER_SECTIONS = null;

/* Report type id for the scheduled variant. Row 16 in att_report_types; 15 is
   the Caregiver Report. */
const REPORT_TYPE_ID = REPORT_TYPE_IDS.SHIFT_BREAKDOWN;

/* ── ReportInfoTip — ⓘ beside the page title ──────────────────────────────
   A HeaderInfoTip (InfoPopover): hover, focus, or a click or tap pins it open,
   so it can be read on a tablet. docs/DUPLICATION_AUDIT.md B31.

   The copy states PURPOSE, not columns. Each metric's mechanics live on its own
   row tooltip, where someone reading a specific number will look. */
const INFO_LINES = [
    'The Shift Event Breakdown compares how each staff shift responds to alerts, to surface staffing gaps behind slow responses, shifts that need training, and where service times or reason codes point at a problem.',
    'Shifts come from this campus’s configured hours, falling back to the standard set. Select a time range to get started.',
];

const ReportInfoTip = () => (
    <HeaderInfoTip
        spaced
        title="About This Report"
        sections={INFO_LINES.map((line) => ({ body: line }))}
        maxWidth={340}
    />
);

function emptyReportFilters() {
    return {
        areas: [],
        areaGroups: [],
        areaTypes: [],
        residents: [],
        residentGroups: [],
        users: [],
        userGroups: [],
        deviceCategories: [],
        devices: [],
        eventTypes: [],
        reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
        timeWindow: { ...DEFAULT_TIME_WINDOW },
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ShiftReportPage = ({ run }) => {

    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());

    // -- Report restoration (history stamp + result cache + nav-menu offer) --
    /* Supplies the navigation state this page used to read directly, and owns
       the router bindings that went with it. */
    const restore = useReportRestore({ reportType: REPORT_TYPE, campusId, autoRun: run });
    const { lookup: cacheLookup, commit: cacheCommit, endRefresh } = restore;
    const navState = restore.navState;
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    const tzOffset = getCampusTzOffset(campusId);
    const isCloud = Boolean(campusConfig?.is_cloud);
    /* Same weight-30 test Alert Details uses, for the same thing: who may switch
       campus. No page-level gate beyond the shared one in Reports.jsx. */
    const showCampusSelector = useMemo(
        () => iAmHigherAuthority(30, session?.role_weight),
        [session],
    );

    // -- Report state --
    const [status, setStatus] = useState('init'); // init | loading | ready | error
    const [records, setRecords] = useState([]);
    const [hours, setHours] = useState([]);
    const [boundaries, setBoundaries] = useState([]);
    const [summary, setSummary] = useState({});
    const [overlimit, setOverlimit] = useState(false);
    const [chartMode, setChartMode] = useState('events');
    /* Stamped when a run COMPLETES, not read from the clock at render time. A
       `new Date()` in the footer would tick forward while the page sat open and
       claim the data was fresher than it is. */
    const [generatedAt, setGeneratedAt] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    /* No sort state. The metrics grid is transposed — shifts are columns in a
       fixed chronological order — so there is nothing to sort. */

    /* Time range. Defaults to Yesterday (range id 2), matching every other
       report page. A single day gives one instance of each shift, which is thin
       for p90 in particular; the p90 cells suppress themselves below the
       backend's threshold and explain why, rather than this page overriding the
       shared default. */
    const initRange = initialRangeState(navState);
    const [rangeId, setRangeId] = useState(initRange.rangeId);
    const [rangeName, setRangeName] = useState(initRange.rangeName);
    const [customRange, setCustomRange] = useState(initRange.customRange);
    const [start, setStart] = useState(() => {
        if (navState?.startDate) {
            const d = new Date(navState.startDate);
            if (!Number.isNaN(d.getTime())) return d;
        }
        const d = getToday(-1); d.setHours(0, 0, 0, 0); return d;
    });
    const [end, setEnd] = useState(() => {
        if (navState?.endDate) {
            const d = new Date(navState.endDate);
            if (!Number.isNaN(d.getTime())) return d;
        }
        const d = getToday(-1); d.setHours(23, 59, 59, 999); return d;
    });

    // -- Time popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);

    // -- Filters --
    const [reportFilters, setReportFilters] = useState(() => {
        const base = emptyReportFilters();
        if (navState?.filters) return { ...base, ...navState.filters };
        return base;
    });
    const [filterOptions, setFilterOptions] = useState({});
    // True once the filter-options fetch has finished, whether or not it succeeded.
    const [optionsSettled, setOptionsSettled] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);

    // -- Schedule drawer --
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);
    const [saving, setSaving] = useState(false);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast(5000);
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // -- Option + frequency loading --
    useEffect(() => {
        let cancelled = false;
        fetchFilterOptions(campusId)
            .then((opts) => { if (!cancelled) { setFilterOptions(opts || {}); setReportFilters((rf) => dropHiddenEventTypes(rf, opts)); setOptionsSettled(true); } })
            .catch(() => { if (!cancelled) { setFilterOptions({}); setOptionsSettled(true); } });
        fetchFrequencies()
            .then((data) => { if (!cancelled) setFrequencies(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setFrequencies([]); });
        return () => { cancelled = true; };
    }, [campusId]);

    // Close the time popover on outside click
    useEffect(() => {
        if (!timePopoverOpen) return undefined;
        const onDocClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [timePopoverOpen]);

    // ----- Time handlers -----
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterOpen(true);
    }, [handleCustomToggle]);

    // ----- Run report -----
    /* Declared ABOVE every hook that lists it as a dependency. A dep array is
       built when its useCallback line executes, so a value declared lower is in
       its temporal dead zone and throws "Cannot access before initialization" on
       first render — which `vite build` does not catch. This has bitten this
       codebase four times. */
    /* `force` skips the cache. Every Run control passes it: a cache that
       ignored an explicit Run would be indistinguishable from a broken button. */
    const handleRunReport = useCallback(async ({ force = false } = {}) => {
        /* Key names match what the state initialisers read off navState -- the
           hook's one contract. The same object becomes the cache key and the
           history stamp, so they cannot disagree about what was run. */
        const runConfig = {
            rangeId,
            filters: reportFilters,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
        };
        const { hit, revalidating } = cacheLookup(runConfig, { force });

        /* A hit renders immediately, stale or not. `hours` and `boundaries`
           ride in `extra`: without them the tables would restore full and the
           shift headers blank. generatedAt comes from the entry's own ranAt so
           a restored report states when it was actually run. */
        if (hit) {
            setRecords(hit.records);
            setHours(hit.extra?.hours || []);
            setBoundaries(hit.extra?.boundaries || []);
            setSummary(hit.summary || {});
            setOverlimit(hit.overLimit);
            setGeneratedAt(new Date(hit.ranAt));
            setErrorMsg('');
            setStatus('ready');
            if (!revalidating) return;
        } else {
            setStatus('loading');
            setErrorMsg('');
        }

        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: expandReasonIds(reportFilters.reasons, filterOptions.reasonCodes).join(','),  // cloud + local ids (utils/reasonFilterIds.js)
            responseTime: reportFilters.responseTime?.enabled
                ? [true, reportFilters.responseTime.operator, reportFilters.responseTime.value]
                : [false, 'lt', 60],
            timeWindow: timeWindowToValues(reportFilters.timeWindow),
        };

        const result = await fetchShiftReport({
            campusId,
            username: getUsername(),
            startDate: formatDateTimeIso(start),
            endDate: formatDateTimeIso(end),
            filters: filterPayload,
        });

        if (result?.ErrorCode) {
            /* A failed BACKGROUND refresh leaves the stale report up rather
               than blanking a report someone is reading. */
            if (revalidating) {
                endRefresh();
                return;
            }
            setRecords([]);
            setHours([]);
            setBoundaries([]);
            setSummary({});
            setStatus('error');
            setErrorMsg(reportErrorMessage(result.ErrorCode));
            return;
        }

        const rows = result.records || [];
        setRecords(rows);
        setHours(result.hours || []);
        setBoundaries(result.boundaries || []);
        setSummary(result.summary || {});
        setOverlimit(Boolean(result.overlimit));
        setGeneratedAt(new Date());
        setStatus('ready');

        /* Caches the result and stamps runConfig into the history entry, so
           browser back lands on something that re-seeds itself. */
        cacheCommit(runConfig, {
            records: rows,
            summary: result.summary || {},
            overLimit: Boolean(result.overlimit),
            csvUrl: '',
            extra: { hours: result.hours || [], boundaries: result.boundaries || [] },
        });
    }, [filterOptions, campusId, start, end, reportFilters, rangeId,
        cacheLookup, cacheCommit, endRefresh]);

    // Auto-run when launched from "Run Report Local" on the scheduled page, or
    // via the /reports/shifts/run route.
    // Armed at mount (consumeAutoRun is a one-shot); fired below once it is safe.
    const autoRunArmedRef = useRef(false);
    useEffect(() => {
        autoRunArmedRef.current = restore.consumeAutoRun();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Fire the armed auto-run. Held until the filter options have settled when the
       run carries a reason filter: the request expands reasons to cloud + local ids
       from those options (utils/reasonFilterIds.js), and firing first would silently
       send cloud ids only and return fewer rows. Without a reason filter it runs
       at once, as before. Declared below handleRunReport so its dep array can list it. */
    useEffect(() => {
        if (!autoRunArmedRef.current) return;
        /* An event type filter waits too: a saved selection may hold a type this report
           no longer lists (utils/reportFilterOptions.js), dropped when the options land. */
        if (((reportFilters.reasons || []).length > 0 || (reportFilters.eventTypes || []).length > 0) && !optionsSettled) return;
        autoRunArmedRef.current = false;
        handleRunReport();
    }, [optionsSettled, reportFilters, handleRunReport]);

    /* Deferred one-shot for "Show that report". No dep array -- the ref gate is
       what makes it fire exactly once, on the render after the seeded state has
       settled. Running synchronously in the click handler would key the cache
       off the OLD config and miss. */
    const pendingRunRef = useRef(false);
    useEffect(() => {
        if (!pendingRunRef.current) return;
        pendingRunRef.current = false;
        handleRunReport();
    }); // runs every render; the ref gate makes it one-shot

    /* Accepting the nav-menu offer. Seeding is page state so it lives here;
       the hook only hands back the config it stored. */
    const handleAcceptRestore = useCallback(() => {
        const cfg = restore.acceptOffer();
        if (!cfg) return;
        const r = rangeStateFor(cfg.rangeId ?? 0);
        setRangeId(r.rangeId);
        setRangeName(r.rangeName);
        setCustomRange(r.customRange);
        if (cfg.startDate) setStart(new Date(cfg.startDate));
        if (cfg.endDate) setEnd(new Date(cfg.endDate));
        if (cfg.filters) setReportFilters(dropHiddenEventTypes(cfg.filters, filterOptions));
        pendingRunRef.current = true;
    }, [restore, filterOptions]);

    // Re-run after a filter apply, but only if the report has already been run
    const shouldRerunRef = useRef(false);
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    /* Range label reused by the footer, the PDF subtitle and the export
       filenames, so a downloaded file cannot describe a different range from the
       page that produced it. */
    const rangeLabel = useMemo(() => {
        const base = customRange
            ? `${formatDateTime12Hour(start)} - ${formatDateTime12Hour(end)}`
            : rangeName;
        const win = describeTimeWindow(reportFilters.timeWindow?.start, reportFilters.timeWindow?.end);
        return win ? `${base}, ${win}` : base;
    }, [customRange, start, end, rangeName, reportFilters.timeWindow]);

    /* Filenames carry the campus and range. A folder of files all called
       "shift-report.csv" is what the browser produces otherwise. */
    const exportStem = useMemo(() => {
        const safe = (v) => String(v || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
        return ['shift-report', safe(campusConfig?.name), safe(rangeLabel)].filter(Boolean).join('_');
    }, [campusConfig, rangeLabel]);

    const handleExportCsv = useCallback(() => {
        exportShiftCsv(records, `${exportStem}.csv`);
    }, [records, exportStem]);

    const handleExportPdf = useCallback(() => {
        exportShiftPdf(records, summary, {
            campusName: campusConfig?.name || '',
            rangeLabel,
            hours,
            boundaries,
            generatedAt: generatedAt
                ? generatedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                : '',
        }, `${exportStem}.pdf`);
    }, [records, summary, hours, boundaries, campusConfig, rangeLabel, generatedAt, exportStem]);

    // ----- Filter drawer -----
    const handleApplyFilters = useCallback((next) => {
        /* timeWindow comes from current state, not the drawer: the drawer leaves it out of
           its Apply payload (the page owns it), so replacing the whole object dropped a
           window set from the header, and the report then ran all day. */
        setReportFilters((prev) => ({ ...next, timeWindow: prev.timeWindow }));
        setFilterOpen(false);
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    const handleClearFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status !== 'init') shouldRerunRef.current = true;
    }, [status]);

    // ----- Schedule -----
    const handleScheduleSave = useCallback(async (payload) => {
        setSaving(true);
        const result = await createSchedule(payload, {
            campusId,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        setSaving(false);
        if (result[0]) {
            showToast('Schedule created successfully.');
            setScheduleOpen(false);
        } else {
            showToast(result[1] || 'Failed to create schedule.', 'error');
        }
    }, [campusId, showToast]);

    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        /* The filter lists hold the old campus's ids, which would still be sent (A21). */
        setReportFilters(clearCampusFilters);
        setStatus('init');
        setRecords([]);
        setHours([]);
        setBoundaries([]);
        setSummary({});
    }, []);

    /* Active filters, not selected items — the same badge on every report (audit A22). */
    const activeFilterCount = useMemo(() => countActiveFilters(reportFilters), [reportFilters]);

    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                {/* sr-page scopes the full-width override for .ar-content — see
                    shift-report-page.css. Without it the content shrink-to-fits
                    and centres, because .ar-content carries margin:0 auto inside
                    a column flex container. */}
                <div className="ps-page sr-page">

                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* PAGE HEADER */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <div className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ps-breadcrumb__current">Shift Breakdown</span>
                                </div>

                                <div className="ps-campus-row">
                                    <label htmlFor="sr-campus-selector" className="ps-campus-label">Campus:</label>
                                    {showCampusSelector && campusList.length > 1 ? (
                                        <select
                                            id="sr-campus-selector"
                                            value={campusId}
                                            onChange={handleCampusChange}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ps-page-title">
                                        Shift Event Breakdown
                                        <ReportInfoTip />
                                    </h1>
                                </div>
                                <div className="ar-title-row__actions">
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* Static custom dates cannot be scheduled — the schedule
                                        recurs, the dates would not. */}
                                    <span className="ar-disabled-tooltip-wrap">
                                        <button
                                            className="dp-btn dp-btn--secondary dp-btn--sm"
                                            onClick={() => setScheduleOpen(true)}
                                            disabled={customRange}
                                        >
                                            <i className="fe fe-calendar" style={{ marginRight: '4px' }} />
                                            Schedule Report
                                        </button>
                                        {customRange ? (
                                            <span className="ar-disabled-tooltip" role="tooltip">
                                                Static custom dates are not eligible for recurring scheduled reporting.
                                            </span>
                                        ) : null}
                                    </span>

                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>

                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={() => handleRunReport({ force: true })}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="ar-content sr-content">
                        {records.length > 0 ? (
                            <div className="sr-export-group">
                                <button
                                    className="dp-btn dp-btn--secondary dp-btn--sm"
                                    onClick={handleExportCsv}
                                    aria-label="Download this report as CSV"
                                >
                                    <i className="fe fe-download" style={{ marginRight: '4px' }} />
                                    CSV
                                </button>
                                <button
                                    className="dp-btn dp-btn--secondary dp-btn--sm"
                                    onClick={handleExportPdf}
                                    aria-label="Download this report as PDF"
                                >
                                    <i className="fe fe-file-text" style={{ marginRight: '4px' }} />
                                    PDF
                                </button>
                            </div>
                        ) : null}

                        {/* A question, not a result: nothing has run in this
                            mount yet, so it sits above the report body. */}
                        <ReportRestoreOffer
                            offer={restore.offer}
                            onAccept={handleAcceptRestore}
                            onDismiss={restore.dismissOffer}
                            show={status === 'init'}
                        />
                        <ReportRestoreNote
                            servedFrom={restore.servedFrom}
                            refreshing={restore.refreshing}
                        />

                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-sunrise" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Select a time range and click <strong>Run Report</strong> to compare shifts.</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={() => handleRunReport({ force: true })} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Run Report
                                </button>
                            </div>
                        ) : null}

                        {status === 'loading' ? (
                            <div className="ar-loading">
                                <div className="ar-loading__spinner" />
                                <p>Loading shift breakdown...</p>
                            </div>
                        ) : null}

                        {status === 'error' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-alert-triangle" style={{ fontSize: '40px', color: 'var(--color-warning)', marginBottom: 'var(--space-4)' }} />
                                <p>{errorMsg}</p>
                                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={() => handleRunReport({ force: true })} style={{ marginTop: 'var(--space-3)' }}>
                                    <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />Try Again
                                </button>
                            </div>
                        ) : null}

                        {status === 'ready' && overlimit ? (
                            /* Says which part was dropped. "Report truncated" with no
                               specifics leaves a reader unsure whether the numbers on
                               screen are complete — the shift metrics are. */
                            <p className="sr-note sr-note--warn">
                                This report is unusually large, so the per-reason breakdown has
                                been omitted. The shift metrics below are complete.
                            </p>
                        ) : null}

                        {status === 'ready' ? (
                            <>
                                <ShiftMetricsTable records={records} summary={summary} hours={hours} />
                                <ShiftHourChart hours={hours} mode={chartMode} onModeChange={setChartMode} />
                                <ShiftChangePanel boundaries={boundaries} summary={summary} />
                                <ShiftReasonTable records={records} isCloud={isCloud} />
                            </>
                        ) : null}

                        {/* Report footer. Primarily breathing room between the last card
                            and the bottom of the viewport — .ar-content scrolls, so
                            without it the final card sits flush against the edge. It
                            carries the run's own context rather than being an empty
                            spacer: a screenshot or a print of this page otherwise says
                            nothing about which campus or range produced it. */}
                        {status === 'ready' ? (
                            <div className="sr-footer">
                                <span className="sr-footer__left">
                                    Shift Event Breakdown
                                    {campusConfig?.name ? ` · ${campusConfig.name}` : ''}
                                    {` · ${rangeLabel}`}
                                </span>
                                <span className="sr-footer__right">
                                    {`${formatCount(summary.total_events)} events across `}
                                    {`${summary.shift_count || 0} ${summary.shift_count === 1 ? 'shift' : 'shifts'}`}
                                    {summary.campus_avg_response_time !== null && summary.campus_avg_response_time !== undefined
                                        ? ` · avg response ${formatMinutes(summary.campus_avg_response_time)} min`
                                        : ''}
                                    {generatedAt
                                        ? ` · generated ${generatedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`
                                        : ''}
                                </span>
                            </div>
                        ) : null}
                    </div>

                    <ReportFilterDrawer
                        open={filterOpen}
                        onClose={() => setFilterOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                        sections={FILTER_SECTIONS}
                        /* showTimeWindow deliberately NOT passed, unlike the Caregiver
                           Report which does offer it. The proc honours the window, so
                           this is not a backend gap — it is that masking the range and
                           then bucketing by shift is actively misleading here. A
                           9am-5pm mask leaves the Night column built from none of its
                           eight hours, while "Hours covered" still reads 11p-7a. The
                           backend withholds the per-hour rates if a window ever
                           arrives via a hand-edited schedule; this page simply never
                           produces one. */
                    />

                    <ScheduleReportDrawer
                        open={scheduleOpen}
                        onClose={() => setScheduleOpen(false)}
                        onSave={handleScheduleSave}
                        saving={saving}
                        reportTypeId={REPORT_TYPE_ID}
                        rangeId={rangeId}
                        rangeName={rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />
                </div>
            </div>
        </>
    );
};

export default ShiftReportPage;
